import { z } from 'zod';

// Define the structured response type for personalized goal plan
export interface GoalPlanAnalysisResponse {
  primaryGoal: {
    title: string;
    description: string;
    focusArea: string;
  };
  subGoals: Array<{
    title: string;
    description: string | null;
    milestone: string | null;
  }>;
  timeline: {
    duration: string;
    phases: Array<{
      phase: string;
      duration: string;
      focus: string;
    }> | null;
  };
  challenges: Array<{
    challenge: string;
    mitigation: string | null;
  }>;
  actionSteps: Array<{
    step: string;
    priority: 'immediate' | 'short-term' | 'long-term';
    category: string | null;
  }>;
  resources: Array<{
    type: 'tool' | 'book' | 'course' | 'other';
    title: string;
    description: string | null;
    link: string | null;
  }> | null;
  keyMilestones: Array<{
    milestone: string;
    timeframe: string;
    description: string | null;
  }> | null;
  weeklyPlan: Array<{
    week: number;
    focus: string;
    tasks: string[];
  }> | null;
}

// Zod schema for validation
export const GoalPlanAnalysisResponseSchema = z.object({
  primaryGoal: z.object({
    title: z.string(),
    description: z.string(),
    focusArea: z.string(),
  }),
  subGoals: z.array(z.object({
    title: z.string(),
    description: z.string().nullable(),
    milestone: z.string().nullable(),
  })),
  timeline: z.object({
    duration: z.string(),
    phases: z.array(z.object({
      phase: z.string(),
      duration: z.string(),
      focus: z.string(),
    })).nullable(),
  }),
  challenges: z.array(z.object({
    challenge: z.string(),
    mitigation: z.string().nullable(),
  })),
  actionSteps: z.array(z.object({
    step: z.string(),
    priority: z.enum(['immediate', 'short-term', 'long-term']),
    category: z.string().nullable(),
  })),
  resources: z.array(z.object({
    type: z.enum(['tool', 'book', 'course', 'other']),
    title: z.string(),
    description: z.string().nullable(),
    link: z.string().nullable(),
  })).nullable(),
  keyMilestones: z.array(z.object({
    milestone: z.string(),
    timeframe: z.string(),
    description: z.string().nullable(),
  })).nullable(),
  weeklyPlan: z.array(z.object({
    week: z.number(),
    focus: z.string(),
    tasks: z.array(z.string()),
  })).nullable(),
});

// Parser function to convert HTML analysis to structured response
export function parseGoalPlanAnalysis(htmlAnalysis: string): GoalPlanAnalysisResponse {
  // This is a simplified parser - in production, you'd use a proper HTML parser
  const cleanText = htmlAnalysis.replace(/<[^>]*>/g, '\n').replace(/\n+/g, '\n');
  
  // Extract sections based on headings
  const extractSection = (heading: string): string => {
    const regex = new RegExp(`${heading}[:\\s]*\\n([^\\n]+)`, 'i');
    const match = cleanText.match(regex);
    return match?.[1]?.trim() || '';
  };
  
  const extractBulletPoints = (sectionTitle: string): string[] => {
    const regex = new RegExp(`${sectionTitle}[:\\s]*\\n([\\s\\S]+?)(?=\\n\\n|\\n[A-Z]|$)`, 'i');
    const match = cleanText.match(regex);
    if (match && match[1]) {
      return match[1]
        .split('\n')
        .filter(line => line.trim())
        .map(line => line.replace(/^[-•*]\s*/, '').trim())
        .filter(line => line.length > 0);
    }
    return [];
  };

  // Parse the HTML into structured data
  const primaryGoalTitle = extractSection('Primary Goal') || "Define and achieve your personalized goals";
  const focusArea = extractSection('Focus Area') || "Personal Growth";
  const timeline = extractSection('Timeline') || "3 months";
  
  const subGoalsList = extractBulletPoints("Sub.?Goals?|Milestones?");
  const challengesList = extractBulletPoints("Challenges?|Obstacles?");
  const actionStepsList = extractBulletPoints("Next Steps?|Action Items?");

  return {
    primaryGoal: {
      title: primaryGoalTitle,
      description: `Your personalized goal focused on ${focusArea}`,
      focusArea: focusArea,
    },
    subGoals: subGoalsList.map(goal => ({
      title: goal,
      description: null,
      milestone: null,
    })),
    timeline: {
      duration: timeline,
      phases: null,
    },
    challenges: challengesList.map(challenge => ({
      challenge: challenge,
      mitigation: null,
    })),
    actionSteps: actionStepsList.map((step, index) => ({
      step: step,
      priority: index === 0 ? 'immediate' : index < 3 ? 'short-term' : 'long-term',
      category: null,
    })),
    resources: null,
    keyMilestones: null,
    weeklyPlan: null,
  };
}

// Alternative: Direct structured output prompt (for future use)
export const STRUCTURED_GOAL_PLAN_PROMPT = `
Return your analysis as a JSON object with the following structure:
{
  "primaryGoal": {
    "title": "Main goal title",
    "description": "Detailed description",
    "focusArea": "Category or area of focus"
  },
  "subGoals": [
    {
      "title": "Sub-goal title",
      "description": "Optional description",
      "milestone": "Optional milestone marker"
    }
  ],
  "timeline": {
    "duration": "Overall timeframe",
    "phases": [
      {
        "phase": "Phase name",
        "duration": "Phase duration",
        "focus": "What to focus on"
      }
    ]
  },
  "challenges": [
    {
      "challenge": "Potential obstacle",
      "mitigation": "How to overcome it"
    }
  ],
  "actionSteps": [
    {
      "step": "Specific action to take",
      "priority": "immediate|short-term|long-term",
      "category": "Optional category"
    }
  ],
  "resources": [
    {
      "type": "tool|book|course|other",
      "title": "Resource name",
      "description": "Why it's helpful",
      "link": "Optional URL"
    }
  ]
}
`;
